import { darkTheme } from '../themes/dark-theme';
import { lightTheme } from '../themes/light-theme';
import type { Theme, ThemeColors } from '../types/theme.types';

// Re-export types for backward compatibility
export type { Theme, ThemeColors } from '../types/theme.types';

// Tailwind classes that are applied based on theme
const tailwindClasses = {
	// Text colors with dark mode
	textPrimary: 'text-gray-900 dark:text-gray-100',
	textSecondary: 'text-gray-600 dark:text-gray-400',
	textTertiary: 'text-gray-400 dark:text-gray-500',
	textMuted: 'text-gray-500 dark:text-gray-400',
	textInverse: 'text-white',
	textDanger: 'text-red-600 dark:text-red-400',
	textError: 'text-red-700 dark:text-red-300',
	textSuccess: 'text-green-600 dark:text-green-400',
	textWarning: 'text-yellow-600 dark:text-yellow-400',
	textInfo: 'text-blue-600 dark:text-blue-400',
	textLink: 'text-blue-600 dark:text-blue-400',
	textLinkHover: 'hover:text-blue-700 dark:hover:text-blue-300',
	// Accent text referenced by merge-variable dropdowns before it shipped
	// (same value as textLink).
	textAccent: 'text-blue-600 dark:text-blue-400',
	textWhite: 'text-white',
	// Text on a solid primary-colored background (e.g. active filter buttons).
	// Consumers referenced this token before it shipped; same convention as
	// textInverse.
	textOnPrimary: 'text-white',

	// Status text colors
	statusSuccess: 'text-green-600 dark:text-green-400',
	statusWarning: 'text-amber-600 dark:text-amber-400',
	statusError: 'text-red-600 dark:text-red-400',
	statusInfo: 'text-blue-600 dark:text-blue-400',

	// Backgrounds
	bgPrimary: 'bg-white dark:bg-gray-800',
	bgSecondary: 'bg-gray-50 dark:bg-gray-900',
	bgTertiary: 'bg-gray-100 dark:bg-gray-800',
	bgContainer: 'bg-white dark:bg-gray-900',
	bgInverse: 'bg-gray-900 dark:bg-gray-100',
	// Default and subtle surfaces. Many newer consumers (softphone capture,
	// voice tooling, phone inputs) read these tokens, but the runtime object
	// never shipped them, so those panels silently rendered with no
	// background. Values mirror bgPrimary / bgSecondary, which match the
	// authored intent (default control surface / softly recessed panel).
	bgDefault: 'bg-white dark:bg-gray-800',
	bgSubtle: 'bg-gray-50 dark:bg-gray-900',

	// Interactive backgrounds
	bgPrimaryHover: 'hover:bg-gray-50 dark:hover:bg-gray-700',
	bgSecondaryHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
	// Ghost-button hover used across rich-text toolbars; referenced before
	// it shipped (same value as bgSecondaryHover).
	hoverButton: 'hover:bg-gray-100 dark:hover:bg-gray-700',
	// More never-shipped tokens referenced by list-builder views (calendar
	// "+N more" hover, grid-layout guide dots, grid field hover wash — the
	// wash's opacity is ramped by the call site, so no baked-in opacity).
	bgHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
	bgMuted: 'bg-gray-300 dark:bg-gray-600',
	bgOverlay: 'bg-gray-900 dark:bg-black',
	// Referenced-before-shipped tokens from the components territory:
	// divide-y list separators, text on the light bgDanger surface (pairs
	// like validationErrorText), and a base surface (pairs with borderBase).
	divider: 'divide-gray-200 dark:divide-gray-700',
	textOnDanger: 'text-red-700 dark:text-red-300',
	backgroundBase: 'bg-white dark:bg-gray-800',
	bgMenuItemActive: 'bg-gray-100 dark:bg-gray-700',
	bgSuccess: 'bg-green-100 dark:bg-green-900/20',
	bgWarning: 'bg-yellow-50 dark:bg-yellow-900/20',
	bgDanger: 'bg-red-100 dark:bg-red-900/20',
	// Hover variant for bgDanger buttons (widget title-edit cancel, delete
	// actions). Referenced by consumers before it shipped; one shade darker,
	// following the bgErrorHover convention below.
	bgDangerHover: 'hover:bg-red-200 dark:hover:bg-red-900/30',
	bgInfo: 'bg-blue-100 dark:bg-blue-900/20',
	// Hover variant for bgInfo buttons (widget title-edit confirm, filter
	// toggles). Referenced by consumers before it shipped.
	bgInfoHover: 'hover:bg-blue-200 dark:hover:bg-blue-900/30',
	bgInfoLight: 'bg-blue-50 dark:bg-blue-900/20', // Legacy - use buttonGroupSelected instead
	// Error backgrounds. The ThemeColors classes type has declared these since
	// the list-builder build-error pass and many consumers (validation panels,
	// reporting aging buckets, cart error rows) already read them — but the
	// runtime object never shipped them, so those surfaces silently rendered
	// with no background. Values follow the established conventions above:
	// solid badge red (statusBadgeError), hover-prefixed variant, light surface.
	bgError: 'bg-red-500 dark:bg-red-600',
	bgErrorHover: 'hover:bg-red-600 dark:hover:bg-red-700',
	bgErrorLight: 'bg-red-50 dark:bg-red-900/20',

	// Borders
	borderDefault: 'border-gray-200 dark:border-gray-600',
	borderStrong: 'border-gray-300 dark:border-gray-600',
	borderLight: 'border-gray-100 dark:border-gray-800',
	borderFocus: 'focus:border-blue-500',
	borderError: 'border-red-500 dark:border-red-400',
	borderWarning: 'border-yellow-400 dark:border-yellow-500',
	borderDisabled: 'border-gray-200 dark:border-gray-700',
	borderInfo: 'border-blue-300 dark:border-blue-700', // Legacy - use buttonGroupSelected instead
	// State borders referenced by form components (quantity selectors,
	// text areas, rich-text toolbars, button groups) before they shipped —
	// className resolved to undefined, so those state borders never
	// rendered. Values mirror the established conventions above.
	borderSuccess: 'border-green-500 dark:border-green-400',
	borderDanger: 'border-red-500 dark:border-red-400',
	borderBase: 'border-gray-200 dark:border-gray-600',
	borderPrimary: 'border-blue-300 dark:border-blue-700',
	borderMuted: 'border-gray-300 dark:border-gray-600',
	borderHover: 'hover:border-gray-300 dark:hover:border-gray-500',
	ringPrimary: 'ring-blue-500',
	// PD-13929: semantic commit rings for the licensing match surfaces (confirm
	// = success ring, reject = error ring) — dark-aware tokens replacing the
	// hardcoded ring-green-500 / ring-red-400 in MatchReviewWizard.
	ringSuccess: 'ring-green-500 dark:ring-green-400',
	ringError: 'ring-red-400 dark:ring-red-500',
	// PD-13929: solid success fill (progress/coverage bars) — replaces the
	// hardcoded bg-green-500 on the Matching page coverage bar.
	bgSuccessSolid: 'bg-green-500 dark:bg-green-500',

	// Button variants
	primaryButton:
		'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white !text-white',
	secondaryButton:
		'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100',
	dangerButton:
		'bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white !text-white',
	// Approval / match-confirmation action. Distinct from `primaryButton`
	// (blue = the view's primary action) — green is reserved for "approve /
	// confirm a match", a semantic success, per the licensing design language.
	successButton:
		'bg-green-500 dark:bg-green-500 hover:bg-green-600 dark:hover:bg-green-600 text-white !text-white',
	outlineButton:
		'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800',
	ghostButton:
		'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',

	// Button group styles
	buttonGroupBase:
		'px-3 py-1.5 rounded text-xs font-medium transition-colors border',
	buttonGroupDefault:
		'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
	buttonGroupSelected:
		'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
	buttonGroupDisabled:
		'bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-50',

	// Input styles
	inputDefault:
		'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100',
	inputError:
		'bg-white dark:bg-gray-800 border-red-500 dark:border-red-400 text-gray-900 dark:text-gray-100',
	inputDisabled:
		'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400',
	inputLabel: 'text-gray-700 dark:text-gray-300',
	inputPlaceholder: 'placeholder:text-gray-400 dark:placeholder:text-gray-400',

	// Table styles
	tableContainer: 'bg-white dark:bg-gray-800',
	tableHeader: 'bg-gray-50 dark:bg-gray-800',
	tableHeaderCell: 'text-gray-900 dark:text-gray-100 font-semibold',
	tableRow: 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700',
	tableRowStripe: 'even:bg-gray-100 dark:even:bg-[#252f3f]',
	tableCell: 'text-gray-900 dark:text-gray-100',
	tableBorder: 'border-gray-200 dark:border-gray-700',
	tableDivider: 'divide-gray-200 dark:divide-gray-700',

	// Tab styles
	tabActive:
		'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-b-2 border-blue-500',
	tabInactive:
		'bg-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100',
	tabBorder: 'border-gray-200 dark:border-gray-700',

	// Chart tooltip styles
	chartTooltipBackground: 'bg-white/95 dark:bg-gray-800/95',
	chartTooltipBorder: 'border-gray-200 dark:border-gray-700',
	chartTooltipText: 'text-gray-700 dark:text-gray-200',
	chartTooltipLabel: 'text-gray-900 dark:text-white',
	chartTooltipShadow: 'shadow-2xl',
	chartTooltipContainer: 'rounded-xl backdrop-blur-sm',

	// Generic tooltip styles (for Tooltip component)
	tooltipBackground: 'bg-gray-800 dark:bg-gray-700',
	tooltipText: 'text-white',
	tooltipBorder: 'border-gray-700 dark:border-gray-600',
	tooltipShadow: 'shadow-2xl',

	// Navigation-specific theme classes - dark navigation regardless of theme
	navBackground: 'bg-gray-900',
	navBorder: 'border-gray-800',
	navItemText: 'text-gray-300',
	navItemTextActive: 'text-white',
	navItemTextHover: 'text-white',
	navItemBg: 'bg-transparent',
	navItemBgHover: 'bg-gray-800',
	navItemBgActive: 'bg-gray-800',
	navGroupText: 'text-gray-400',
	navIconDefault: 'text-gray-400',
	navIconActive: 'text-white',
	navIconHover: 'text-gray-200',
	navSeparator: 'bg-gray-800',
	navUserMenuBg: 'bg-gray-800',
	navUserMenuHover: 'bg-gray-700',
	navUserMenuText: 'text-gray-100',
	navBadgeBg: 'bg-red-500',

	// Navigation scrollbar
	navScrollbarTrack: 'bg-gray-800 dark:bg-gray-900',
	navScrollbarThumb: 'bg-gray-600 dark:bg-gray-700',
	navScrollbarThumbHover: 'bg-gray-500 dark:bg-gray-600',

	// Legacy navigation styles (for backward compatibility)
	navItemPrimary:
		'bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white',
	navItemPrimaryActive: 'bg-gray-800 text-white',
	navItemPrimaryHover: 'hover:bg-gray-800 hover:text-white',
	navItemSecondary:
		'bg-transparent text-gray-400 hover:bg-gray-800 hover:text-white',
	navItemSecondaryActive: 'bg-gray-800 text-white',
	navItemSecondaryHover: 'hover:bg-gray-800 hover:text-white',

	// Container styles
	containerPrimary: 'bg-white dark:bg-gray-800',
	containerSecondary: 'bg-gray-50 dark:bg-gray-900',
	containerTertiary: 'bg-gray-100 dark:bg-gray-700',
	containerInverse: 'bg-gray-900 dark:bg-white',

	// Filter styles
	filterBackground: 'bg-gray-50 dark:bg-gray-800',
	filterBackgroundHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
	filterContainer: 'bg-white dark:bg-gray-900',
	filterPill: 'bg-gray-200 dark:bg-gray-700',
	filterPillActive: 'bg-blue-500 dark:bg-blue-600 text-white',
	filterPillHover: 'hover:bg-gray-300 dark:hover:bg-gray-600',
	filterPillText: 'text-gray-700 dark:text-gray-300',
	filterPillActiveText: 'text-white',

	// Overlay and backdrop with proper light/dark mode support
	backdropOverlay: 'bg-gray-900/40 dark:bg-black/60 backdrop-blur-sm',
	modalPanel: 'bg-white dark:bg-gray-800', // Explicit opaque backgrounds

	// Validation states
	validationErrorBg: 'bg-red-100 dark:bg-red-900/20',
	validationWarningBg: 'bg-yellow-50 dark:bg-yellow-900/20',
	validationSuccessBg: 'bg-green-100 dark:bg-green-900/20',
	validationInfoBg: 'bg-blue-100 dark:bg-blue-900/20',
	validationErrorBorder: 'border-red-300 dark:border-red-700',
	validationWarningBorder: 'border-yellow-300 dark:border-yellow-700',
	validationSuccessBorder: 'border-green-300 dark:border-green-700',
	validationInfoBorder: 'border-blue-300 dark:border-blue-700',
	validationErrorText: 'text-red-700 dark:text-red-300',
	validationWarningText: 'text-yellow-700 dark:text-yellow-300',
	validationSuccessText: 'text-green-700 dark:text-green-300',
	validationInfoText: 'text-blue-700 dark:text-blue-300',

	// Toast backgrounds
	toastSuccessBg: 'bg-green-100 dark:bg-green-900',
	toastWarningBg: 'bg-amber-100 dark:bg-amber-900',
	toastErrorBg: 'bg-red-100 dark:bg-red-900',
	toastInfoBg: 'bg-blue-100 dark:bg-blue-900',

	// Grid gap
	gridGap: 'gap-4',

	// Padding utilities
	paddingSm: 'p-2',
	paddingMd: 'p-4',
	paddingLg: 'p-6',
	paddingXl: 'p-8',

	// Margin utilities
	marginSm: 'm-2',
	marginMd: 'm-4',
	marginLg: 'm-6',
	marginXl: 'm-8',

	// Border radius utilities
	roundedSm: 'rounded-sm',
	roundedMd: 'rounded-md',
	roundedLg: 'rounded-lg',
	roundedXl: 'rounded-xl',
	roundedFull: 'rounded-full',

	// Focus ring styles
	focusRingDefault:
		'focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 focus:ring-offset-0',
	focusRingPrimary:
		'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-0',
	focusRingDanger:
		'focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:ring-offset-0',
	focusRingSuccess:
		'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:ring-offset-0',
	focusRingWarning:
		'focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 focus:ring-offset-0',
	focusRingNone: 'focus:outline-none',

	// Status-specific classes
	statusOrderOpen: 'bg-blue-500 dark:bg-blue-600 text-white',
	statusOrderProcessing: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusOrderShipped: 'bg-sky-500 dark:bg-sky-600 text-white',
	statusOrderDelivered: 'bg-green-500 dark:bg-green-600 text-white',
	statusOrderCancelled: 'bg-red-500 dark:bg-red-600 text-white',
	statusOrderDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	statusInvoiceOpen: 'bg-blue-500 dark:bg-blue-600 text-white',
	statusInvoicePartial: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusInvoicePaid: 'bg-green-500 dark:bg-green-600 text-white',
	statusInvoiceOverdue: 'bg-red-600 dark:bg-red-700 text-white',
	statusInvoiceCancelled: 'bg-gray-500 dark:bg-gray-600 text-white',
	statusInvoiceDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	statusPaymentPaid: 'bg-green-600 dark:bg-green-500 text-white',
	statusPaymentPending: 'bg-amber-600 dark:bg-amber-500 text-white',
	statusPaymentPartial: 'bg-blue-600 dark:bg-blue-500 text-white',
	statusPaymentFailed: 'bg-red-600 dark:bg-red-500 text-white',
	statusPaymentRefunded: 'bg-purple-600 dark:bg-purple-500 text-white',
	statusPaymentDefault: 'bg-gray-600 dark:bg-gray-500 text-white',

	statusCampaignActive: 'bg-green-500 dark:bg-green-600 text-white',
	statusCampaignScheduled: 'bg-blue-500 dark:bg-blue-600 text-white',
	statusCampaignPaused: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusCampaignCompleted: 'bg-gray-500 dark:bg-gray-600 text-white',
	statusCampaignFailed: 'bg-red-500 dark:bg-red-600 text-white',
	statusCampaignDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	statusLotActive: 'bg-green-500 dark:bg-green-600 text-white',
	statusLotExpired: 'bg-red-500 dark:bg-red-600 text-white',
	statusLotQuarantine: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusLotDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	statusStockHigh: 'bg-green-500 dark:bg-green-600 text-white',
	statusStockMedium: 'bg-blue-500 dark:bg-blue-600 text-white',
	statusStockLow: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusStockCritical: 'bg-red-500 dark:bg-red-600 text-white',
	statusStockDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	// Generic status badge styles
	statusBadgeSuccess: 'bg-green-500 dark:bg-green-600 text-white',
	statusBadgeWarning: 'bg-amber-500 dark:bg-amber-600 text-white',
	statusBadgeError: 'bg-red-500 dark:bg-red-600 text-white',
	statusBadgeInfo: 'bg-blue-500 dark:bg-blue-600 text-white',
	statusBadgeDefault: 'bg-gray-500 dark:bg-gray-600 text-white',

	// Light variant status badges
	statusBadgeSuccessLight:
		'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800',
	statusBadgeWarningLight:
		'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800',
	statusBadgeErrorLight:
		'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800',
	statusBadgeInfoLight:
		'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800',
	statusBadgeDefaultLight:
		'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-800',

	// Badge component variants
	badgePrimary:
		'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
	badgeSecondary:
		'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
	badgeSuccess:
		'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
	badgeWarning:
		'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
	badgeDanger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
	badgeInfo: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
	badgeDefault: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',

	// Form-specific theming classes
	formFieldBase: 'rounded-md shadow-sm transition-colors duration-200',
	formFieldDefault:
		'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400',
	// Explicit placeholder styling for consistency
	placeholderGray400: 'placeholder-gray-400',
	formFieldFocus:
		'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none',
	formFieldError:
		'bg-white dark:bg-gray-800 border border-red-500 dark:border-red-400 text-gray-900 dark:text-gray-100 focus:border-red-500 dark:focus:border-red-400 focus:ring-2 focus:ring-red-500/20',
	formFieldDisabled:
		'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-75',

	// Connected dropdown states
	formFieldConnectedTop: 'rounded-t-md rounded-b-none border-b-transparent',
	formFieldConnectedBottom: 'rounded-t-none rounded-b-md border-t-0',

	// Select-specific classes
	selectMenu:
		'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg',
	selectOption: 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100',
	selectOptionSelected:
		'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
	selectOptionFocus:
		'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100',

	// MultiSelect tag classes
	multiSelectTag:
		'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
	multiSelectTagFixed:
		'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400',

	// Chip/pill colors for round-robin assignment
	chipColor1:
		'bg-blue-300 dark:bg-blue-900 text-blue-900 dark:text-blue-100 border border-blue-400 dark:border-blue-800',
	chipColor2:
		'bg-green-300 dark:bg-green-900 text-green-900 dark:text-green-100 border border-green-400 dark:border-green-800',
	chipColor3:
		'bg-amber-300 dark:bg-amber-900 text-amber-900 dark:text-amber-100 border border-amber-400 dark:border-amber-800',
	chipColor4:
		'bg-purple-300 dark:bg-purple-900 text-purple-900 dark:text-purple-100 border border-purple-400 dark:border-purple-800',
	chipColor5:
		'bg-red-300 dark:bg-red-900 text-red-900 dark:text-red-100 border border-red-400 dark:border-red-800',
	chipColor6:
		'bg-pink-300 dark:bg-pink-900 text-pink-900 dark:text-pink-100 border border-pink-400 dark:border-pink-800',
	chipColor7:
		'bg-indigo-300 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border border-indigo-400 dark:border-indigo-800',
	chipColor8:
		'bg-teal-300 dark:bg-teal-900 text-teal-900 dark:text-teal-100 border border-teal-400 dark:border-teal-800',
	chipColor9:
		'bg-orange-300 dark:bg-orange-900 text-orange-900 dark:text-orange-100 border border-orange-400 dark:border-orange-800',
	chipColor10:
		'bg-sky-300 dark:bg-sky-900 text-sky-900 dark:text-sky-100 border border-sky-400 dark:border-sky-800',
	chipColor11:
		'bg-violet-300 dark:bg-violet-900 text-violet-900 dark:text-violet-100 border border-violet-400 dark:border-violet-800',
	chipColor12:
		'bg-cyan-300 dark:bg-cyan-900 text-cyan-900 dark:text-cyan-100 border border-cyan-400 dark:border-cyan-800',
	chipColor13:
		'bg-lime-300 dark:bg-lime-900 text-lime-900 dark:text-lime-100 border border-lime-400 dark:border-lime-800',
	chipColor14:
		'bg-yellow-300 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100 border border-yellow-400 dark:border-yellow-800',
	chipColor15:
		'bg-emerald-300 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100 border border-emerald-400 dark:border-emerald-800',
	chipColor16:
		'bg-rose-300 dark:bg-rose-900 text-rose-900 dark:text-rose-100 border border-rose-400 dark:border-rose-800',

	// Agent chat UI classes
	agentMessageBg: 'bg-gray-100 dark:bg-gray-700',
	agentMessageText: 'text-gray-900 dark:text-gray-100',
	userMessageBg: 'bg-blue-600',
	userMessageText: 'text-white',
	dangerButtonText: 'text-white',
};

// Form theme values for react-select and other JS-based styling
export const formTheme = {
	values: {
		light: {
			background: '#FFFFFF',
			backgroundDisabled: '#F9FAFB', // gray-50
			backgroundHover: '#F9FAFB', // gray-50
			backgroundSelected: '#EFF6FF', // blue-50
			border: '#D1D5DB', // gray-300
			borderHover: '#9CA3AF', // gray-400 (mirrors radioBorderHover)
			borderDisabled: '#E5E7EB', // gray-200
			borderFocus: '#3B82F6', // blue-500
			borderError: '#EF4444', // red-500
			borderWarning: '#FACC15', // yellow-400
			text: '#111827', // gray-900
			textDisabled: '#6B7280', // gray-500
			textPlaceholder: '#9CA3AF', // gray-400
			menuBackground: '#FFFFFF',
			menuBorder: '#E5E7EB', // gray-200
			optionHover: '#F9FAFB', // gray-50
			optionSelected: '#EFF6FF', // blue-50
			optionSelectedText: '#2563EB', // blue-600
			tagBackground: '#F3F4F6', // gray-100
			tagBackgroundFixed: '#E5E7EB', // gray-200
			tagText: '#374151', // gray-700
			tagTextFixed: '#4B5563', // gray-600
			// Radio button specific colors
			radioBorder: '#D1D5DB', // gray-300
			radioBorderHover: '#9CA3AF', // gray-400
			radioBorderChecked: '#3B82F6', // blue-500
			radioBorderDisabled: '#E5E7EB', // gray-200
			radioBackground: '#FFFFFF',
			radioBackgroundHover: '#F9FAFB', // gray-50
			radioBackgroundChecked: '#FFFFFF',
			radioBackgroundDisabled: '#F9FAFB', // gray-50
			radioIndicator: '#3B82F6', // blue-500
			radioIndicatorDisabled: '#9CA3AF', // gray-400
		},
		dark: {
			background: '#1F2937', // gray-800
			backgroundDisabled: '#111827', // gray-900
			backgroundHover: '#374151', // gray-700
			backgroundSelected: 'rgba(30, 58, 138, 0.3)', // blue-900/30
			border: '#4B5563', // gray-600
			borderHover: '#D1D5DB', // gray-300 (mirrors radioBorderHover)
			borderDisabled: '#374151', // gray-700
			borderFocus: '#60A5FA', // blue-400
			borderError: '#F87171', // red-400
			borderWarning: '#EAB308', // yellow-500
			text: '#F9FAFB', // gray-100
			textDisabled: '#9CA3AF', // gray-400
			textPlaceholder: '#9CA3AF', // gray-400
			menuBackground: '#1F2937', // gray-800
			menuBorder: '#374151', // gray-700
			optionHover: '#374151', // gray-700
			optionSelected: 'rgba(30, 58, 138, 0.3)', // blue-900/30
			optionSelectedText: '#60A5FA', // blue-400
			tagBackground: '#374151', // gray-700
			tagBackgroundFixed: '#4B5563', // gray-600
			tagText: '#D1D5DB', // gray-300
			tagTextFixed: '#9CA3AF', // gray-400
			// Radio button specific colors
			radioBorder: '#9CA3AF', // gray-400 - Better contrast on gray-700 background
			radioBorderHover: '#D1D5DB', // gray-300 - Even better on hover
			radioBorderChecked: '#60A5FA', // blue-400
			radioBorderDisabled: '#6B7280', // gray-500
			radioBackground: '#1F2937', // gray-800
			radioBackgroundHover: '#374151', // gray-700
			radioBackgroundChecked: '#1F2937', // gray-800
			radioBackgroundDisabled: '#111827', // gray-900
			radioIndicator: '#60A5FA', // blue-400
			radioIndicatorDisabled: '#6B7280', // gray-500
		},
	},
};

// Helper function to enrich theme with Tailwind classes
function enrichThemeWithClasses(theme: Theme): Theme {
	return {
		...theme,
		colors: {
			...theme.colors,
			classes: tailwindClasses,
		},
	};
}

// Theme name type
export const themes = {
	light: enrichThemeWithClasses(lightTheme),
	dark: enrichThemeWithClasses(darkTheme),
} as const;

export type ThemeName = keyof typeof themes;

// Whitelist of valid theme keys. Used to reject anything that isn't an
// own-property string key on `themes` — defends against `themeName === '__proto__'`
// (which would otherwise return Object.prototype from `themes[themeName]`),
// `'constructor'`, plain objects with a `toString` that returns a real theme
// name, and any non-string input.
const VALID_THEME_NAMES: ReadonlySet<ThemeName> = new Set<ThemeName>([
	'light',
	'dark',
]);

// Theme service singleton
export class ThemeService {
	private static instance: ThemeService;
	private currentTheme: Theme;
	private listeners: Array<(theme: Theme) => void> = [];

	constructor() {
		this.currentTheme = themes.light;
	}

	static getInstance(): ThemeService {
		if (!ThemeService.instance) {
			ThemeService.instance = new ThemeService();
		}
		return ThemeService.instance;
	}

	getCurrentTheme(): Theme {
		return this.currentTheme;
	}

	setTheme(themeName: ThemeName): void {
		// Validate against the explicit whitelist before indexing into `themes`.
		// `themes['__proto__']` would otherwise return Object.prototype and
		// corrupt `currentTheme`.
		if (
			typeof themeName !== 'string' ||
			!VALID_THEME_NAMES.has(themeName as ThemeName)
		) {
			return;
		}
		const theme = themes[themeName];
		if (theme !== this.currentTheme) {
			this.currentTheme = theme;
			this.notifyListeners();
		}
	}

	toggleTheme(): void {
		const newTheme = this.currentTheme.name === 'light' ? 'dark' : 'light';
		this.setTheme(newTheme);
	}

	subscribe(listener: (theme: Theme) => void): () => void {
		// Reject non-function listeners up front so the failure surfaces at the
		// subscribe call site, not later inside notifyListeners where it would
		// look like a runtime crash from an unrelated theme change.
		if (typeof listener !== 'function') {
			throw new TypeError(
				'ThemeService.subscribe requires a function listener',
			);
		}
		this.listeners.push(listener);
		return () => {
			this.listeners = this.listeners.filter((l) => l !== listener);
		};
	}

	private notifyListeners(): void {
		// Iterate over a snapshot so a listener that subscribes/unsubscribes
		// during notification doesn't shift the iteration. Each listener call
		// is isolated: a throw in one must not prevent the rest from running
		// (a single buggy subscriber should not break theme propagation across
		// the whole app).
		const snapshot = this.listeners.slice();
		snapshot.forEach((listener) => {
			try {
				listener(this.currentTheme);
			} catch (err) {
				console.error('ThemeService: listener threw during notify', err);
			}
		});
	}

	// Helper method to get colors
	getColors(): ThemeColors {
		// Return a fresh shallow-copy of the inner objects on every call so a
		// caller mutating `colors.values.primary = ...` only affects the copy
		// they hold — the next getColors() call yields a clean, original-valued
		// object. Freezing was an alternative but throws in strict mode and
		// breaks consumers that probe-then-discard.
		return {
			...this.currentTheme.colors,
			values: { ...this.currentTheme.colors.values },
			classes: { ...tailwindClasses },
		};
	}

	// Helper method to check if current theme is dark
	isDark(): boolean {
		return this.currentTheme.name === 'dark';
	}

	// Static methods for backward compatibility
	static lightTheme = lightTheme;
	static darkTheme = darkTheme;

	static getTheme(isDark: boolean): Theme {
		return isDark ? darkTheme : lightTheme;
	}

	static getColors(isDark: boolean): ThemeColors {
		return this.getTheme(isDark).colors;
	}
}

// Export singleton instance
export const themeService = ThemeService.getInstance();

/**
 * Look up a value in a status map without falling through to `Object.prototype`
 * properties. `someMap[status]` returns `Object.prototype` for `'__proto__'`
 * and the `Object` constructor for `'constructor'` — both truthy, both
 * silently corrupt the resulting className. This helper short-circuits any
 * key that isn't a true own-property string.
 */
function _safeStatusLookup(
	statusMap: Record<string, string>,
	status: unknown,
): string | undefined {
	let key: string;
	if (typeof status === 'string') {
		key = status;
	} else if (typeof status === 'number' && Number.isFinite(status)) {
		// Numeric status codes (e.g. order status `1`) are commonly looked up
		// against string keys (`'1'`). Coerce here rather than burdening every
		// callsite, but only for safe finite numbers.
		key = String(status);
	} else {
		return undefined;
	}
	if (key === '') {
		return undefined;
	}
	return Object.prototype.hasOwnProperty.call(statusMap, key)
		? statusMap[key]
		: undefined;
}

// Status color helper functions
export function getOrderStatusClasses(status?: string): string {
	const statusMap: Record<string, string> = {
		'1': tailwindClasses.statusOrderOpen, // Unsubmitted - blue
		'2': tailwindClasses.statusOrderProcessing, // Pending - amber
		'3': tailwindClasses.statusOrderShipped, // Sent to ERP - sky
		'4': tailwindClasses.statusOrderDelivered, // Approved - green
		'Partially Shipped': tailwindClasses.statusOrderProcessing, // Partially Shipped - amber
		'5': tailwindClasses.statusOrderDelivered, // Shipped - green (success)
		'6': tailwindClasses.statusOrderProcessing, // Held - amber
		'7': tailwindClasses.statusOrderCancelled, // Cancelled - red
		'8': tailwindClasses.statusOrderProcessing, // Approval Required - amber
		'9': tailwindClasses.statusOrderCancelled, // Override Rejected - red
		'10': tailwindClasses.statusOrderProcessing, // Cancellation Requested - amber
		'11': tailwindClasses.statusOrderDelivered, // Override Approved - green
		'12': tailwindClasses.statusOrderOpen, // Submitted - blue
		'13': tailwindClasses.statusOrderShipped, // Quote - sky
	};
	return (
		_safeStatusLookup(statusMap, status) ?? tailwindClasses.statusOrderDefault
	);
}

export function getInvoiceStatusClasses(status?: string): string {
	const statusMap: Record<string, string> = {
		'1': tailwindClasses.statusInvoiceOpen,
		'2': tailwindClasses.statusInvoicePartial,
		'3': tailwindClasses.statusInvoicePaid,
		'4': tailwindClasses.statusInvoiceCancelled,
		OPEN: tailwindClasses.statusInvoiceOpen,
		PARTIAL: tailwindClasses.statusInvoicePartial,
		PAID: tailwindClasses.statusInvoicePaid,
		OVERDUE: tailwindClasses.statusInvoiceOverdue,
		CANCELLED: tailwindClasses.statusInvoiceCancelled,
	};
	return (
		_safeStatusLookup(statusMap, status) ?? tailwindClasses.statusInvoiceDefault
	);
}

export function getPaymentStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		paid: tailwindClasses.statusPaymentPaid,
		completed: tailwindClasses.statusPaymentPaid,
		succeeded: tailwindClasses.statusPaymentPaid,
		pending: tailwindClasses.statusPaymentPending,
		processing: tailwindClasses.statusPaymentPending,
		'processing refund': tailwindClasses.statusPaymentPending,
		partial: tailwindClasses.statusPaymentPartial,
		'partially refunded': tailwindClasses.statusPaymentPartial,
		failed: tailwindClasses.statusPaymentFailed,
		declined: tailwindClasses.statusPaymentFailed,
		refunded: tailwindClasses.statusPaymentRefunded,
		voided: 'bg-gray-600 dark:bg-gray-700 text-white',
		cancelled: 'bg-gray-600 dark:bg-gray-700 text-white',
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusPaymentDefault
	);
}

export function getCampaignStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusCampaignActive,
		running: tailwindClasses.statusCampaignActive,
		scheduled: tailwindClasses.statusCampaignScheduled,
		pending: tailwindClasses.statusCampaignScheduled,
		paused: tailwindClasses.statusCampaignPaused,
		completed: tailwindClasses.statusCampaignCompleted,
		failed: tailwindClasses.statusCampaignFailed,
		error: tailwindClasses.statusCampaignFailed,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusCampaignDefault
	);
}

export function getCampaignContactStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	switch (normalizedStatus) {
		case 'sent':
			return 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300';
		case 'pending':
			return 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200';
		case 'error':
			return 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400';
		default:
			return 'bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400';
	}
}

export function getLotStatusClasses(status?: string): string {
	const normalizedStatus = status?.toLowerCase();
	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusLotActive,
		expired: tailwindClasses.statusLotExpired,
		recalled: tailwindClasses.statusLotExpired,
		quarantine: tailwindClasses.statusLotQuarantine,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusLotDefault
	);
}

export function getStockLevelClasses(
	level: 'high' | 'medium' | 'low' | 'critical',
): string {
	const levelMap: Record<string, string> = {
		high: tailwindClasses.statusStockHigh,
		medium: tailwindClasses.statusStockMedium,
		low: tailwindClasses.statusStockLow,
		critical: tailwindClasses.statusStockCritical,
	};
	return (
		_safeStatusLookup(levelMap, level) ?? tailwindClasses.statusStockDefault
	);
}

export function getStatusBadgeClasses(
	status?: string,
	variant: 'solid' | 'light' = 'solid',
): string {
	const normalizedStatus = status?.toLowerCase();

	if (variant === 'light') {
		const statusMap: Record<string, string> = {
			active: tailwindClasses.statusBadgeSuccessLight,
			completed: tailwindClasses.statusBadgeSuccessLight,
			success: tailwindClasses.statusBadgeSuccessLight,
			delivered: tailwindClasses.statusBadgeSuccessLight,
			paid: tailwindClasses.statusBadgeSuccessLight,
			closed: tailwindClasses.statusBadgeSuccessLight,
			pending: tailwindClasses.statusBadgeWarningLight,
			processing: tailwindClasses.statusBadgeWarningLight,
			'in-progress': tailwindClasses.statusBadgeWarningLight,
			partial: tailwindClasses.statusBadgeWarningLight,
			inactive: tailwindClasses.statusBadgeInfoLight,
			draft: tailwindClasses.statusBadgeInfoLight,
			open: tailwindClasses.statusBadgeInfoLight,
			new: tailwindClasses.statusBadgeInfoLight,
			error: tailwindClasses.statusBadgeErrorLight,
			failed: tailwindClasses.statusBadgeErrorLight,
			cancelled: tailwindClasses.statusBadgeErrorLight,
			overdue: tailwindClasses.statusBadgeErrorLight,
		};
		return (
			_safeStatusLookup(statusMap, normalizedStatus) ??
			tailwindClasses.statusBadgeDefaultLight
		);
	}

	const statusMap: Record<string, string> = {
		active: tailwindClasses.statusBadgeSuccess,
		completed: tailwindClasses.statusBadgeSuccess,
		success: tailwindClasses.statusBadgeSuccess,
		delivered: tailwindClasses.statusBadgeSuccess,
		paid: tailwindClasses.statusBadgeSuccess,
		closed: tailwindClasses.statusBadgeSuccess,
		pending: tailwindClasses.statusBadgeWarning,
		processing: tailwindClasses.statusBadgeWarning,
		'in-progress': tailwindClasses.statusBadgeWarning,
		partial: tailwindClasses.statusBadgeWarning,
		inactive: tailwindClasses.statusBadgeInfo,
		draft: tailwindClasses.statusBadgeInfo,
		open: tailwindClasses.statusBadgeInfo,
		new: tailwindClasses.statusBadgeInfo,
		error: tailwindClasses.statusBadgeError,
		failed: tailwindClasses.statusBadgeError,
		cancelled: tailwindClasses.statusBadgeError,
		overdue: tailwindClasses.statusBadgeError,
	};
	return (
		_safeStatusLookup(statusMap, normalizedStatus) ??
		tailwindClasses.statusBadgeDefault
	);
}

export function getChipColor(index: number): string {
	const colorIndex = (index % 16) + 1;
	const colorKey = `chipColor${colorIndex}` as keyof typeof tailwindClasses;
	return tailwindClasses[colorKey] || tailwindClasses.chipColor1;
}

export function getChipColorByValue(theme: Theme, index: number): string {
	const colorIndex = (index % 16) + 1;
	const colorKey = `chipColor${colorIndex}` as keyof typeof theme.colors.values;
	return theme.colors.values[colorKey] || theme.colors.values.chipColor1;
}
